'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ownerState", "children", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { useComposerContext } from "./internals/ComposerContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A `<label>` element for the conversation input textarea.
 *
 * Pair this with `Composer.TextArea` to create a proper
 * label-for-input association. This lets screen readers announce the
 * label when the textarea receives focus, and lets users click the label
 * to focus the textarea.
 *
 * ```tsx
 * const textareaId = useId();
 *
 * <Composer.Root>
 *   <Composer.Label htmlFor={textareaId}>
 *     Ask anything
 *   </Composer.Label>
 *   <Composer.TextArea id={textareaId} />
 * </Composer.Root>
 * ```
 *
 * When `children` is omitted the label text falls back to the locale
 * string `composerInputAriaLabel` — the same default used by the textarea's
 * own `aria-label` — so you get consistent announcements without duplication.
 * In that case you may want to visually hide the label while keeping it in
 * the accessibility tree (e.g. via a `.sr-only` utility class).
 */
export const ComposerLabel = /*#__PURE__*/React.forwardRef(function ComposerLabel(props, ref) {
  const _ref = props,
    {
      ownerState: ownerStateProp,
      children,
      slots,
      slotProps
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  void ownerStateProp;
  const composer = useComposerContext();
  const localeText = useChatLocaleText();
  const ownerState = {
    isSubmitting: composer.isSubmitting,
    hasValue: composer.hasValue,
    isStreaming: composer.isStreaming,
    attachmentCount: composer.attachmentCount,
    disabled: composer.disabled
  };
  const Label = slots?.label ?? 'label';
  const labelProps = useSlotProps({
    elementType: Label,
    externalSlotProps: slotProps?.label,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref
    }
  });
  return /*#__PURE__*/_jsx(Label, _extends({}, labelProps, {
    children: children ?? localeText.composerInputAriaLabel
  }));
});
if (process.env.NODE_ENV !== "production") ComposerLabel.displayName = "ComposerLabel";